/**
 * Duplicates Page
 * Find and resolve duplicate documents (same SHA-256 checksum).
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  Copy,
  FileIcon,
  Layers,
  Link2,
  Loader2,
  Tag,
  Trash2,
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { getDuplicates, resolveDuplicate } from '../api';
import type { DuplicateDocument, DuplicateGroup } from '../api';
import { formatBytes } from '@/lib/utils';

function formatDate(dateStr: string | null): string {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('en-CA');
}

function DuplicateDocCard({
  doc,
  isOnly,
  onKeep,
  onDelete,
  isResolving,
}: {
  doc: DuplicateDocument;
  isOnly: boolean;
  onKeep: () => void;
  onDelete: () => void;
  isResolving: boolean;
}) {
  return (
    <div className="border rounded-lg p-4 flex-1 min-w-0 bg-white dark:bg-card">
      <div className="flex items-start gap-3 mb-3">
        <FileIcon className="h-5 w-5 text-muted-foreground flex-shrink-0 mt-0.5" />
        <div className="min-w-0">
          <p className="font-medium text-sm truncate" title={doc.name}>
            {doc.name}
          </p>
          <p className="text-xs text-muted-foreground truncate" title={doc.originalName}>
            {doc.originalName}
          </p>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-2 text-xs text-muted-foreground mb-4">
        <div className="flex items-center gap-1.5">
          <Layers className="h-3.5 w-3.5" />
          <span>{doc.versionCount} version{doc.versionCount !== 1 ? 's' : ''}</span>
        </div>
        <div className="flex items-center gap-1.5">
          <Link2 className="h-3.5 w-3.5" />
          <span>{doc.linkCount} link{doc.linkCount !== 1 ? 's' : ''}</span>
        </div>
        <div className="flex items-center gap-1.5">
          <Tag className="h-3.5 w-3.5" />
          <span>{doc.sourceSize ? formatBytes(doc.sourceSize) : '—'}</span>
        </div>
        <div>Created {formatDate(doc.createdAt)}</div>
      </div>

      <div className="flex gap-2">
        {!isOnly && (
          <Button
            variant="default"
            size="sm"
            className="flex-1 bg-green-600 hover:bg-green-700"
            disabled={isResolving}
            onClick={onKeep}
          >
            <CheckCircle2 className="h-3.5 w-3.5 mr-1.5" />
            Keep This
          </Button>
        )}
        <Button
          variant="outline"
          size="sm"
          className="flex-1 text-red-600 border-red-300 hover:bg-red-50 dark:hover:bg-red-950/20"
          disabled={isResolving}
          onClick={onDelete}
        >
          <Trash2 className="h-3.5 w-3.5 mr-1.5" />
          Delete
        </Button>
      </div>
    </div>
  );
}

function DuplicateGroupCard({ group }: { group: DuplicateGroup }) {
  const [expanded, setExpanded] = useState(true);
  const [confirmAction, setConfirmAction] = useState<{
    type: 'keep' | 'delete';
    doc: DuplicateDocument;
  } | null>(null);
  const queryClient = useQueryClient();

  const resolveMutation = useMutation({
    mutationFn: resolveDuplicate,
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['dms-duplicates'] });
      queryClient.invalidateQueries({ queryKey: ['documents'] });
      const action = variables.action === 'keep' ? 'Kept document, deleted duplicates' : 'Document deleted';
      toast.success(action);
    },
    onError: (error) => {
      toast.error('Failed to resolve', { description: error instanceof Error ? error.message : 'Unknown error' });
    },
  });

  const handleConfirm = () => {
    if (!confirmAction) return;
    if (confirmAction.type === 'keep') {
      const deleteIds = group.documents
        .filter((d) => d.documentId !== confirmAction.doc.documentId)
        .map((d) => d.documentId);
      resolveMutation.mutate({
        action: 'keep',
        keepDocumentId: confirmAction.doc.documentId,
        deleteDocumentIds: deleteIds,
      });
    } else {
      resolveMutation.mutate({
        action: 'keep',
        keepDocumentId: group.documents.find((d) => d.documentId !== confirmAction.doc.documentId)!.documentId,
        deleteDocumentIds: [confirmAction.doc.documentId],
      });
    }
    setConfirmAction(null);
  };

  const shortChecksum = group.checksum.slice(0, 12);

  return (
    <>
      <div className="bg-white dark:bg-card border rounded-lg overflow-hidden">
        <button
          type="button"
          onClick={() => setExpanded(!expanded)}
          className="w-full px-4 py-3 flex items-center gap-3 hover:bg-muted/50 transition-colors text-left"
        >
          {expanded ? (
            <ChevronDown className="h-4 w-4 flex-shrink-0" />
          ) : (
            <ChevronRight className="h-4 w-4 flex-shrink-0" />
          )}
          <span className="font-mono text-xs text-muted-foreground">{shortChecksum}...</span>
          <span className="text-sm font-medium truncate">{group.documents[0]?.originalName || 'Unknown'}</span>
          <Badge variant="warning" className="ml-auto shrink-0">
            {group.documents.length} copies
          </Badge>
          {resolveMutation.isPending && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
        </button>

        {expanded && (
          <div className="px-4 pb-4 pt-1">
            <div className="flex gap-4 overflow-x-auto">
              {group.documents.map((doc) => (
                <DuplicateDocCard
                  key={doc.documentId}
                  doc={doc}
                  isOnly={group.documents.length <= 1}
                  onKeep={() => setConfirmAction({ type: 'keep', doc })}
                  onDelete={() => setConfirmAction({ type: 'delete', doc })}
                  isResolving={resolveMutation.isPending}
                />
              ))}
            </div>
          </div>
        )}
      </div>

      <AlertDialog open={!!confirmAction} onOpenChange={(open) => !open && setConfirmAction(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {confirmAction?.type === 'keep' ? 'Keep this document?' : 'Delete this document?'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {confirmAction?.type === 'keep' ? (
                <>
                  Keep <strong>{confirmAction.doc.name}</strong> and delete{' '}
                  {group.documents.length - 1} duplicate{group.documents.length - 1 !== 1 ? 's' : ''}.
                  All links, tags, and references will be transferred to the kept document.
                </>
              ) : (
                <>
                  Permanently delete <strong>{confirmAction?.doc.name}</strong>.
                  Links and tags will NOT be transferred.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirm}
              className={confirmAction?.type === 'delete' ? 'bg-red-600 hover:bg-red-700' : ''}
            >
              {confirmAction?.type === 'keep' ? 'Keep & Delete Duplicates' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

export function DuplicatesPage() {
  const queryClient = useQueryClient();

  const { data, isLoading, error } = useQuery({
    queryKey: ['dms-duplicates'],
    queryFn: getDuplicates,
  });

  const groups: DuplicateGroup[] = data?.data ?? [];
  const totalDuplicates = groups.reduce((sum, g) => sum + g.documents.length - 1, 0);

  const resolveAllMutation = useMutation({
    mutationFn: async () => {
      let deleted = 0;
      for (const group of groups) {
        const sorted = [...group.documents].sort((a, b) => {
          if (b.linkCount !== a.linkCount) return b.linkCount - a.linkCount;
          if (b.versionCount !== a.versionCount) return b.versionCount - a.versionCount;
          return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
        });
        const keep = sorted[0];
        const deleteIds = sorted.slice(1).map((d) => d.documentId);
        const result = await resolveDuplicate({
          action: 'keep',
          keepDocumentId: keep.documentId,
          deleteDocumentIds: deleteIds,
        });
        deleted += result.deletedCount;
      }
      return deleted;
    },
    onSuccess: (deleted) => {
      toast.success(`Cleaned up ${deleted} duplicate${deleted !== 1 ? 's' : ''}`);
      queryClient.invalidateQueries({ queryKey: ['dms-duplicates'] });
      queryClient.invalidateQueries({ queryKey: ['documents'] });
    },
    onError: (error) => {
      toast.error('Cleanup failed', { description: error instanceof Error ? error.message : 'Unknown error' });
    },
  });

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-background">
      {/* Header */}
      <div className="bg-white dark:bg-card border-b border-gray-200 dark:border-border px-6 py-4">
        <div className="flex items-center gap-4">
          <Link
            to="/documents"
            className="flex items-center gap-2 text-sm text-gray-600 dark:text-muted-foreground hover:text-gray-900 dark:hover:text-foreground transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Documents
          </Link>
        </div>
        <div className="mt-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-gray-100 dark:bg-muted rounded-lg">
              <Copy className="h-6 w-6 text-gray-600 dark:text-muted-foreground" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-foreground">
                Duplicate Detection
              </h1>
              <p className="text-sm text-gray-600 dark:text-muted-foreground mt-1">
                Find and resolve documents with identical file content (same SHA-256 hash)
              </p>
            </div>
          </div>
          {groups.length > 0 && (
            <Button
              variant="destructive"
              size="sm"
              disabled={resolveAllMutation.isPending}
              onClick={() => resolveAllMutation.mutate()}
            >
              {resolveAllMutation.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Cleaning up...
                </>
              ) : (
                <>
                  <Trash2 className="h-4 w-4 mr-2" />
                  Remove all {totalDuplicates} duplicate{totalDuplicates !== 1 ? 's' : ''}
                </>
              )}
            </Button>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="max-w-6xl mx-auto px-6 py-8">
        {isLoading && (
          <div className="flex items-center justify-center py-16 text-muted-foreground gap-2">
            <Loader2 className="h-5 w-5 animate-spin" />
            <span>Scanning for duplicates...</span>
          </div>
        )}

        {error && (
          <div className="text-center py-16 text-red-500">
            Failed to load duplicates: {(error as Error).message}
          </div>
        )}

        {!isLoading && !error && groups.length === 0 && (
          <div className="text-center py-16 text-muted-foreground">
            <Copy className="h-12 w-12 mx-auto mb-4 opacity-30" />
            <p className="text-sm font-medium">No duplicate documents found</p>
            <p className="text-xs mt-1">Documents are compared by file checksum (SHA-256).</p>
          </div>
        )}

        {!isLoading && !error && groups.length > 0 && (
          <>
            <div className="flex items-center gap-2 mb-4 text-sm text-muted-foreground">
              <span>{groups.length} group{groups.length !== 1 ? 's' : ''}</span>
              <span>·</span>
              <span>{totalDuplicates} duplicate{totalDuplicates !== 1 ? 's' : ''} to remove</span>
            </div>
            <div className="space-y-4">
              {groups.map((group) => (
                <DuplicateGroupCard key={group.checksum} group={group} />
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
